import * as React from 'react';
import { compose, setDisplayName, withState, withHandlers } from 'recompose';
import { translate } from 'react-i18next';
import { TranslateProps } from 'util/i18n';
import { ReportSettings, ReportSettingsChangeHandler } from 'logic/reports/ReportSettings';
import { DwellTime } from './DwellTime';
import { SensitivityHelpDialog } from './SensitivityHelpDialog';
import { Button } from 'ui/shared/Button';
import { Input } from 'ui/shared/Input';

export type OwnProps = {
    reportSettings: Readonly<ReportSettings>;
    onChange: ReportSettingsChangeHandler;
};

type RowProps = TranslateProps & {
    id: string;
    enabled: boolean;
    onChangeEnabled: React.ChangeEventHandler<HTMLInputElement>;
    textKey: string;
    children: React.ReactNode;
};

const inputStyle = {
    marginLeft: '1em',
    width: '5em',
};
const helpButtonStyle = {
    margin: '.5em 0 0 1em',
};
const disabledStyle = {
    opacity: .25,
};

const Row = ({ id, enabled, onChangeEnabled, textKey, t, children }: RowProps) =>
    <tr>
        <td>
            <label>
                <input
                    type="checkbox"
                    id={ id }
                    checked={ enabled }
                    onChange={ onChangeEnabled }
                />
                { t(textKey) }
            </label>
        </td>
        <td style={ enabled ? undefined : disabledStyle }>
            { children }
        </td>
    </tr>
;

const RawSensitivitySection = ({
    minIntersectionsEnabled,
    maxIntersectionsEnabled,
    minDwellTimeEnabled,
    maxDwellTimeEnabled,
    dwellThresholdEnabled,
    handleChangeMinIntersectionsEnabled,
    handleChangeMaxIntersectionsEnabled,
    handleChangeMinDwellTimeEnabled,
    handleChangeMaxDwellTimeEnabled,
    handleChangeDwellThresholdEnabled,
    minIntersections,
    maxIntersections,
    minDwellTime,
    maxDwellTime,
    dwellThreshold,
    handleChangeMinIntersections,
    handleChangeMaxIntersections,
    handleChangeMinDwellTime,
    handleChangeMaxDwellTime,
    handleChangeDwellThreshold,
    helpVisible,
    handleClickHelp,
    handleCloseHelp,
    t,
}: OwnProps & RecomposeProps & TranslateProps) =>
    <table>
        <tbody>
            <Row
                id="sensitivity-min-intersections-enabled"
                enabled={ minIntersectionsEnabled }
                onChangeEnabled={ handleChangeMinIntersectionsEnabled }
                textKey="6.1241.0.70"
                t={ t }
            >
                <Input
                    type="number"
                    id="sensitivity-min-intersections"
                    value={ minIntersections }
                    onChange={ handleChangeMinIntersections }
                    min={ 0 }
                    style={ inputStyle }
                />
            </Row>
            <Row
                id="sensitivity-max-intersections-enabled"
                enabled={ maxIntersectionsEnabled }
                onChangeEnabled={ handleChangeMaxIntersectionsEnabled }
                textKey="6.1241.0.71"
                t={ t }
            >
                <Input
                    type="number"
                    id="sensitivity-max-intersections"
                    value={ maxIntersections }
                    onChange={ handleChangeMaxIntersections }
                    min={ 0 }
                    style={ inputStyle }
                />
            </Row>
            <Row
                id="sensitivity-min-dwell-time-enabled"
                enabled={ minDwellTimeEnabled }
                onChangeEnabled={ handleChangeMinDwellTimeEnabled }
                textKey="6.1241.0.72"
                t={ t }
            >
                <DwellTime
                    id="sensitivity-min-dwell-time"
                    value={ minDwellTime }
                    onChange={ handleChangeMinDwellTime }
                />
            </Row>
            <Row
                id="sensitivity-max-dwell-time-enabled"
                enabled={ maxDwellTimeEnabled }
                onChangeEnabled={ handleChangeMaxDwellTimeEnabled }
                textKey="6.1241.0.73"
                t={ t }
            >
                <DwellTime
                    id="sensitivity-max-dwell-time"
                    value={ maxDwellTime }
                    onChange={ handleChangeMaxDwellTime }
                />
            </Row>
            <Row
                id="sensitivity-dwell-threshold-enabled"
                enabled={ dwellThresholdEnabled }
                onChangeEnabled={ handleChangeDwellThresholdEnabled }
                textKey="Report_Sensitivity_DwellThreshold"
                t={ t }
            >
                <DwellTime
                    id="sensitivity-dwell-threshold"
                    value={ dwellThreshold }
                    onChange={ handleChangeDwellThreshold }
                />
            </Row>
            <tr>
                <td/>
                <td>
                    <Button
                        type="button"
                        id="sensitivity-help-show-button"
                        onClick={ handleClickHelp }
                        textKey="6.9622.3990"
                        style={ helpButtonStyle }
                    />
                    <SensitivityHelpDialog
                        visible={ helpVisible }
                        onClose={ handleCloseHelp }
                    />
                </td>
            </tr>
        </tbody>
    </table>
;

type RecomposeProps = {
    minIntersectionsEnabled: boolean;
    maxIntersectionsEnabled: boolean;
    minDwellTimeEnabled: boolean;
    maxDwellTimeEnabled: boolean;
    dwellThresholdEnabled: boolean;
    handleChangeMinIntersectionsEnabled: React.ChangeEventHandler<HTMLInputElement>;
    handleChangeMaxIntersectionsEnabled: React.ChangeEventHandler<HTMLInputElement>;
    handleChangeMinDwellTimeEnabled: React.ChangeEventHandler<HTMLInputElement>;
    handleChangeMaxDwellTimeEnabled: React.ChangeEventHandler<HTMLInputElement>;
    handleChangeDwellThresholdEnabled: React.ChangeEventHandler<HTMLInputElement>;
    minIntersections: string;
    maxIntersections: string;
    minDwellTime: number;
    maxDwellTime: number;
    dwellThreshold: number;
    handleChangeMinIntersections: React.ChangeEventHandler<HTMLInputElement>;
    handleChangeMaxIntersections: React.ChangeEventHandler<HTMLInputElement>;
    handleChangeMinDwellTime: (n: number) => void;
    handleChangeMaxDwellTime: (n: number) => void;
    handleChangeDwellThreshold: (n: number) => void;
    helpVisible: boolean;
    handleClickHelp: () => void;
    handleCloseHelp: () => void;
};
type _RecomposeProps = RecomposeProps & {
    _setMinIntersectionsEnabled: (b: boolean) => void;
    _setMaxIntersectionsEnabled: (b: boolean) => void;
    _setMinDwellTimeEnabled: (b: boolean) => void;
    _setMaxDwellTimeEnabled: (b: boolean) => void;
    _setDwellThresholdEnabled: (b: boolean) => void;
    _setMinIntersections: (s: string) => void;
    _setMaxIntersections: (s: string) => void;
    _setMinDwellTime: (n: number) => void;
    _setMaxDwellTime: (n: number) => void;
    _setDwellThreshold: (n: number) => void;
    _setHelpVisible: (b: boolean) => void;
};

export const SensitivitySection = compose<OwnProps & RecomposeProps & TranslateProps, OwnProps>(
    setDisplayName('SensitivitySection'),
    translate(),
    withState('minIntersectionsEnabled', '_setMinIntersectionsEnabled', ({ reportSettings }: OwnProps) =>
        reportSettings.minIntersections > 0
    ),
    withState('maxIntersectionsEnabled', '_setMaxIntersectionsEnabled', ({ reportSettings }: OwnProps) =>
        reportSettings.maxIntersections > 0
    ),
    withState('minDwellTimeEnabled', '_setMinDwellTimeEnabled', ({ reportSettings }: OwnProps) =>
        reportSettings.minDwell >= 0
    ),
    withState('maxDwellTimeEnabled', '_setMaxDwellTimeEnabled', ({ reportSettings }: OwnProps) =>
        reportSettings.maxDwell >= 0
    ),
    withState('dwellThresholdEnabled', '_setDwellThresholdEnabled', ({ reportSettings }: OwnProps) =>
        reportSettings.dwellThreshold >= 0
    ),
    withState('minIntersections', '_setMinIntersections', ({ reportSettings, minIntersectionsEnabled }: OwnProps & RecomposeProps) =>
        minIntersectionsEnabled
            ? reportSettings.minIntersections
            : ReportSettings.defaultMinIntersections
    ),
    withState('maxIntersections', '_setMaxIntersections', ({ reportSettings, maxIntersectionsEnabled }: OwnProps & RecomposeProps) =>
        maxIntersectionsEnabled
            ? reportSettings.maxIntersections
            : ReportSettings.defaultMaxIntersections
    ),
    withState('minDwellTime', '_setMinDwellTime', ({ reportSettings, minDwellTimeEnabled }: OwnProps & RecomposeProps) =>
        minDwellTimeEnabled
            ? reportSettings.minDwell
            : ReportSettings.defaultMinDwell
    ),
    withState('maxDwellTime', '_setMaxDwellTime', ({ reportSettings, maxDwellTimeEnabled }: OwnProps & RecomposeProps) =>
        maxDwellTimeEnabled
            ? reportSettings.maxDwell
            : ReportSettings.defaultMaxDwell
    ),
    withState('dwellThreshold', '_setDwellThreshold', ({ reportSettings, dwellThresholdEnabled }: OwnProps & RecomposeProps) =>
        dwellThresholdEnabled
            ? reportSettings.dwellThreshold
            : ReportSettings.defaultDwellThreshold
    ),
    withState('helpVisible', '_setHelpVisible', false),
    withHandlers<OwnProps & _RecomposeProps, Partial<RecomposeProps>>({
        handleChangeMinIntersectionsEnabled: ({ onChange, minIntersections, _setMinIntersectionsEnabled }) => (e: React.ChangeEvent<HTMLInputElement>) => { // tslint:disable-line
            const enabled = e.target.checked;
            _setMinIntersectionsEnabled(enabled);
            onChange(reportSettings => reportSettings.setMinIntersections(enabled ? parseInt(minIntersections, 10) : ReportSettings.noIntersections));
        },
        handleChangeMaxIntersectionsEnabled: ({ onChange, maxIntersections, _setMaxIntersectionsEnabled }) => (e: React.ChangeEvent<HTMLInputElement>) => { // tslint:disable-line
            const enabled = e.target.checked;
            _setMaxIntersectionsEnabled(enabled);
            onChange(reportSettings => reportSettings.setMaxIntersections(enabled ? parseInt(maxIntersections, 10) : ReportSettings.noIntersections));
        },
        handleChangeMinDwellTimeEnabled: ({ onChange, minDwellTime, _setMinDwellTimeEnabled }) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const enabled = e.target.checked;
            _setMinDwellTimeEnabled(enabled);
            onChange(reportSettings => reportSettings.setMinDwell(enabled ? minDwellTime : ReportSettings.noDwell));
        },
        handleChangeMaxDwellTimeEnabled: ({ onChange, maxDwellTime, _setMaxDwellTimeEnabled }) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const enabled = e.target.checked;
            _setMaxDwellTimeEnabled(enabled);
            onChange(reportSettings => reportSettings.setMaxDwell(enabled ? maxDwellTime : ReportSettings.noDwell));
        },
        handleChangeDwellThresholdEnabled: ({ onChange, dwellThreshold, _setDwellThresholdEnabled }) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const enabled = e.target.checked;
            _setDwellThresholdEnabled(enabled);
            onChange(reportSettings => reportSettings.setDwellThreshold(enabled ? dwellThreshold : ReportSettings.noDwell));
        },
        handleChangeMinIntersections: ({ onChange, maxIntersections, _setMinIntersections, _setMaxIntersections, _setMinIntersectionsEnabled }) => (e: React.ChangeEvent<HTMLInputElement>) => { // tslint:disable-line
            const minIntersections = parseInt(e.target.value, 10);
            _setMinIntersections(e.target.value);
            if (isNaN(minIntersections)) {
                return;
            }

            // Disable the setting if the user drops the value below 1
            const enabled = minIntersections > 0;
            _setMinIntersectionsEnabled(enabled);
            
            onChange(reportSettings => {
                reportSettings = reportSettings.setMinIntersections(enabled ? minIntersections : ReportSettings.noIntersections);
            
                // Don't allow min to go above max
                if (enabled && minIntersections > parseInt(maxIntersections, 10)) {
                    reportSettings = reportSettings.setMaxIntersections(minIntersections);
                    _setMaxIntersections(minIntersections.toString());
                }

                return reportSettings;
            });
        },
        handleChangeMaxIntersections: ({ onChange, minIntersections, _setMinIntersections, _setMaxIntersections, _setMaxIntersectionsEnabled }) => (e: React.ChangeEvent<HTMLInputElement>) => { // tslint:disable-line
            const maxIntersections = parseInt(e.target.value, 10);
            _setMaxIntersections(e.target.value);

            // Disable the setting if the user drops the value below 1
            const enabled = maxIntersections > 0;
            _setMaxIntersectionsEnabled(enabled);

            onChange(reportSettings => {
                reportSettings = reportSettings.setMaxIntersections(enabled ? maxIntersections : ReportSettings.noIntersections);
            
                // Don't allow max to go below min
                if (enabled && maxIntersections < parseInt(minIntersections, 10)) {
                    reportSettings = reportSettings.setMinIntersections(maxIntersections);
                    _setMinIntersections(maxIntersections.toString());
                }
    
                return reportSettings;
            });
        },
        handleChangeMinDwellTime: ({ onChange, maxDwellTime, _setMinDwellTime, _setMaxDwellTime, _setMinDwellTimeEnabled }) => (n: number) => {
            const enabled = n > 0;
            _setMinDwellTimeEnabled(enabled);
           
            onChange(reportSettings => {
                reportSettings = reportSettings.setMinDwell(n);
            
                // Ensure that max is above min.
                if (enabled && n > maxDwellTime) {
                    const newMaxDwell = n + 60000;
                    _setMaxDwellTime(newMaxDwell);
                    reportSettings = reportSettings.setMaxDwell(newMaxDwell);
                }
    
                return reportSettings;
            });
        },
        handleChangeMaxDwellTime: ({ onChange, minDwellTime, _setMinDwellTime, _setMaxDwellTime, _setMaxDwellTimeEnabled }) => (n: number) => {
            const enabled = n > 0;
            _setMaxDwellTimeEnabled(enabled);
           
            onChange(reportSettings => {
                reportSettings = reportSettings.setMaxDwell(n);
            
                // Ensure that max is above min.
                if (enabled && n < minDwellTime) {
                    const newMinDwell = Math.floor(n / 2000) * 1000;
                    _setMinDwellTime(newMinDwell);
                    reportSettings = reportSettings.setMinDwell(newMinDwell);
                }
                
                return reportSettings;
            });
        },
        handleChangeDwellThreshold: ({ onChange, _setDwellThreshold, _setDwellThresholdEnabled }) => (n: number) => {
            _setDwellThreshold(n);
            const enabled = n >= 0;
            _setDwellThresholdEnabled(enabled);
            onChange(reportSettings => reportSettings.setDwellThreshold(enabled ? n : ReportSettings.noDwell));
        },
        handleClickHelp: ({ _setHelpVisible }) => () => {
            _setHelpVisible(true);
        },
        handleCloseHelp: ({ _setHelpVisible }) => () => {
            _setHelpVisible(false);
        },
    }),
)(RawSensitivitySection);



// WEBPACK FOOTER //
// ./src/ui/pages/designer/sections/sensitivity/SensitivitySection.tsx